import { Link } from 'react-router-dom'
import { CaretRight } from '@phosphor-icons/react'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import { Badge, Card, CardBody, CardHeader } from '@/ui'
import { cn } from '@/lib/cn'
import type { ProcessState, RunView } from '../projection'
import { computeBalance, formatBp, pctToBp, type BalanceKind } from '../model/balance'
import { runFigures } from '../model/rules'
import { Line, StreamBadge, SyncBadges } from './bits'
import { useKgPct } from './useText'
import { stepName } from './labels'

const STATUS_TONE: Record<string, 'neutral' | 'ok' | 'warn' | 'danger' | 'info'> = {
  OPEN: 'info',
  RUNNING: 'info',
  CLOSED: 'neutral',
  BALANCED: 'ok',
  EXCEPTION: 'warn',
  CANCELLED: 'neutral',
}

export function RunStatusBadge({ run }: { run: RunView }) {
  const { t } = useI18n()
  return <Badge tone={STATUS_TONE[run.status] ?? 'neutral'}>{t(`process.status.${run.status}` as MessageKey)}</Badge>
}

/** A run in a list: machine, step, origin, status and its balance once closed. */
export function RunListItem({ state, run }: { state: ProcessState; run: RunView }) {
  const { locale, t } = useI18n()
  const fmt = useFormat()
  const machine = state.machines.get(run.equipment_id)
  const fig = runFigures(run, state.steps.get(run.step_code))
  return (
    <li>
      <Link
        to={`/m/process/run/${run.id}`}
        className="flex items-center gap-3 border-b border-rule/70 py-3 last:border-b-0"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-mono text-[15px] font-bold">{machine?.code ?? '—'}</span>
            <span className="text-[14px]">{stepName(state, run.step_code, locale)}</span>
            <span className="font-mono text-[12px] text-muted">{run.code}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px] text-muted">
            <RunStatusBadge run={run} />
            <StreamBadge stream={run.stream} />
            {run.farmer_code && <span className="font-mono">{run.farmer_code}</span>}
            {run.crop_name && <span>{run.crop_name}</span>}
            <span className="tabular">
              {t('process.bagsIn', { n: run.inputs.length })} · {fmt.weight(fig.input_g)}
            </span>
            <SyncBadges sync={run.sync} />
          </div>
        </div>
        <CaretRight aria-hidden="true" className="shrink-0 text-muted" />
      </Link>
    </li>
  )
}

/**
 * Provisional balance on the phone (before sync) or the server's result (after): kilos in, each
 * output, left over against the step's limit and yield against the machine's range (F6.6-e).
 */
export function BalancePanel({ state, run }: { state: ProcessState; run: RunView }) {
  const { t } = useI18n()
  const kgPct = useKgPct()
  const step = state.steps.get(run.step_code)
  const band = state.pack.yield_bands.find((b) => b.id === run.yield_band_id) ?? null
  const fig = runFigures(run, step)
  const loss_limit_bp = pctToBp(step?.loss_limit_pct) ?? 50
  const local = computeBalance(fig, {
    loss_limit_bp,
    min_bp: pctToBp(band?.min_pct),
    max_bp: pctToBp(band?.max_pct),
  })
  const server = run.balance
  const synced = !!server?.result
  const residual_g = synced ? (server.residual_g ?? local.residual_g) : local.residual_g
  const residual_bp = synced ? (pctToBp(server.residual_pct) ?? local.residual_bp) : local.residual_bp
  const yield_bp = synced ? (pctToBp(server.yield_pct) ?? local.yield_bp) : local.yield_bp
  const kinds = (synced ? server.kinds : local.kinds) as BalanceKind[]
  const pass = synced ? server.result === 'BALANCED' : local.pass
  const minBp = pctToBp(synced ? (server.min_pct ?? band?.min_pct) : band?.min_pct)
  const maxBp = pctToBp(synced ? (server.max_pct ?? band?.max_pct) : band?.max_pct)
  const limitBp = synced ? (pctToBp(server.loss_limit_pct) ?? loss_limit_bp) : loss_limit_bp
  const p = (g: number) => (fig.input_g > 0 ? Math.round((g * 10_000) / fig.input_g) : null)

  return (
    <Card>
      <CardHeader
        eyebrow={t(synced ? 'process.balance.synced' : 'process.balance.provisional')}
        actions={
          fig.input_g > 0 ? (
            <Badge tone={pass ? 'ok' : 'warn'}>{t(pass ? 'process.balance.pass' : 'process.balance.held')}</Badge>
          ) : undefined
        }
      />
      <CardBody className="py-1">
        <Line label={t('process.balance.in')} value={kgPct(synced ? server.input_g : fig.input_g, null)} strong />
        <Line label={t('process.balance.flush')} value={kgPct(synced ? server.flush_g : fig.flush_g, p(fig.flush_g))} />
        <Line
          label={t('process.balance.product')}
          value={kgPct(synced ? server.product_g : fig.product_g, p(fig.product_g))}
        />
        <Line
          label={t('process.balance.byproduct')}
          value={kgPct(synced ? server.byproduct_g : fig.byproduct_g, p(fig.byproduct_g))}
        />
        <Line label={t('process.balance.waste')} value={kgPct(synced ? server.reject_g : fig.reject_g, p(fig.reject_g))} />
        <Line
          label={t('process.balance.leftOver', { limit: formatBp(limitBp) })}
          value={kgPct(residual_g, residual_bp)}
          strong
          tone={kinds.some((k) => k !== 'YIELD') ? 'warn' : fig.input_g > 0 ? 'ok' : undefined}
        />
        {yield_bp !== null && (
          <Line
            label={
              minBp !== null && maxBp !== null
                ? t('process.balance.yieldRange', { min: formatBp(minBp, true), max: formatBp(maxBp, true) })
                : t('process.balance.yield')
            }
            value={formatBp(yield_bp, true)}
            tone={kinds.includes('YIELD') ? 'warn' : 'ok'}
          />
        )}
        {kinds.length > 0 && fig.input_g > 0 && (
          <div className="flex flex-wrap gap-1.5 py-2">
            {kinds.map((k) => (
              <Badge key={k} tone="warn">
                {t(`process.balance.kind.${k}` as MessageKey)}
              </Badge>
            ))}
          </div>
        )}
        {!synced && fig.input_g > 0 && (
          <p className={cn('pb-2 text-[12px] text-muted')}>{t('process.balance.provisionalHint')}</p>
        )}
      </CardBody>
    </Card>
  )
}
